import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppNavigation } from './hooks/useAppNavigation';
import { LanguagePicker } from './LanguagePicker';
import { ThemePicker } from './ThemePicker';
import { openSearchOverlay } from './SearchOverlay';

const MobileNav: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const navProps = useAppNavigation();
  const { t } = useTranslation();

  // Close menu on route change
  useEffect(() => {
    setOpen(false);
    setProductsOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const go = (path: string) => {
    navigate(path);
    setOpen(false);
  };

  // Match against translated label so the lookup keeps working after i18n init.
  const productChildren = navProps.links.find(l => l.label === t('nav.platform'))?.children ?? [];

  return (
    <>
      {/* ─── Top bar ─────────────────────────────────────── */}
      <div className="mnav-bar">
        <div className="mnav-bar-left">
          {/* Hamburger */}
          <button className="mnav-hamburger" onClick={() => setOpen(!open)} aria-label={t('mobile.menu')}>
            {open ? (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            ) : (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            )}
          </button>
          <a href="/" style={{ display: 'flex' }}>
            <img src="/landing-assets/lunarCrush-header-logo.png" alt="LunarCrush" width={120} height={28} style={{ width: 120, height: 28, objectFit: 'contain' as const }} />
          </a>
        </div>
        <div className="mnav-bar-right">
          {/* DEV-11278 / DEV-11294: search entry point — opens the in-place search overlay */}
          <button className="mnav-icon-btn" onClick={openSearchOverlay} aria-label={t('common.search')}>
            <svg width="22" height="22" viewBox="0 0 256 256" fill="currentColor">
              <path d="M229.66,218.34l-50.07-50.06a88.11,88.11,0,1,0-11.31,11.31l50.06,50.07a8,8,0,0,0,11.32-11.32ZM40,112a72,72,0,1,1,72,72A72.08,72.08,0,0,1,40,112Z" />
            </svg>
          </button>
          {navProps.userMenu ? (
            <>
              {/* APP button */}
              <button className="mnav-signup-btn" onClick={() => navProps.userMenu!.onAppPress()}>
                {t('nav.app').toUpperCase()}
              </button>
              {/* User avatar icon */}
              <button className="mnav-icon-btn" onClick={() => navProps.userMenu!.onAvatarPress()} aria-label={t('nav.account')}>
                <svg width="22" height="22" viewBox="0 0 256 256" fill="currentColor">
                  <path d="M230.92,212c-15.23-26.33-38.7-45.21-66.09-54.16a72,72,0,1,0-73.66,0C63.78,166.78,40.31,185.66,25.08,212a8,8,0,1,0,13.85,8c18.84-32.56,52.14-52,89.07-52s70.23,19.44,89.07,52a8,8,0,1,0,13.85-8ZM72,96a56,56,0,1,1,56,56A56.06,56.06,0,0,1,72,96Z" />
                </svg>
              </button>
            </>
          ) : (
            <>
              {/* DEV-12734: single GET STARTED button (was SIGN UP + sign-in icon) */}
              <button className="mnav-signup-btn" onClick={() => navProps.cta.onPress?.()}>
                {t('nav.getStarted').toUpperCase()}
              </button>
            </>
          )}
        </div>
      </div>

      {/* ─── Overlay menu (portaled to body to escape sticky parent) ── */}
      {open && createPortal(
        <div className="mnav-overlay" onClick={() => setOpen(false)}>
          <div className="mnav-panel" onClick={(e) => e.stopPropagation()}>
            {/* Panel header: close X (left, on the closed-state hamburger's
                spot) + theme toggle (right, the slot the X vacated). */}
            <div className="mnav-panel-header">
              <button className="mnav-close" onClick={() => setOpen(false)} aria-label={t('mobile.close')}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
              <ThemePicker variant="compact" />
            </div>

            {/* Home link */}
            <button className="mnav-item" onClick={() => go('/')}>{t('mobile.home')}</button>

            {/* Product dropdown */}
            <button className="mnav-item mnav-item-parent" onClick={() => setProductsOpen(!productsOpen)}>
              <span>{t('mobile.product')}</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ transform: productsOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {productsOpen && (
              <div className="mnav-sub">
                {productChildren.map((child: { label: string; onPress?: () => void }) => (
                  <button key={child.label} className="mnav-sub-item" onClick={() => { child.onPress?.(); setOpen(false); }}>
                    {child.label}
                  </button>
                ))}
              </div>
            )}

            {/* Top-level links */}
            <button className="mnav-item" onClick={() => go('/pricing')}>{t('nav.pricing')}</button>
            <button className="mnav-item" onClick={() => go('/enterprise')}>{t('nav.enterprise')}</button>
            <button className="mnav-item" onClick={() => go('/use-cases')}>{t('nav.useCases')}</button>
            <button className="mnav-item" onClick={() => go('/blog')}>{t('nav.blog')}</button>

            {/* Spacer pushes auth to bottom */}
            <div style={{ flex: 1 }} />

            {/* Language picker — opens a dropdown anchored inside the drawer. */}
            <div className="mnav-language">
              <LanguagePicker variant="full" />
            </div>

            {/* Auth buttons */}
            <div className="mnav-auth">
              {navProps.userMenu ? (
                <>
                  <button className="mnav-login" onClick={() => { navProps.userMenu!.onAvatarPress(); setOpen(false); }}>{t('nav.account').toUpperCase()}</button>
                  <button className="mnav-signup" onClick={() => { navProps.userMenu!.onAppPress(); setOpen(false); }}>{t('nav.app').toUpperCase()}</button>
                </>
              ) : (
                <>
                  {/* DEV-12734: single GET STARTED button (was LOG IN + SIGN UP) */}
                  <button className="mnav-signup" onClick={() => { navProps.cta.onPress?.(); setOpen(false); }}>{t('nav.getStarted').toUpperCase()}</button>
                </>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default MobileNav;
